import React, { useState, useMemo } from 'react';
import DefaultModal from 'components/Utils/DefaultModal';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DefaultChip from 'components/Utils/DefaultChip';
import TagInput from 'components/Utils/TagInput';
import CPPUtils from 'services/CPPUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import Icon from 'components/Utils/NewIcon';
import { DrugInputView } from '../NewPrescription/view';
import AddToFavoritesModal from '../AddToFavoritesModal';
import './style.scss';
import ConfirmationIcon from '../../../Utils/NewIcon/ConfirmationIcon';
import { PrescriptionMedication } from '../../../../generated/api';
import UnreadBadge from '../../../Common/UnreadBadge';

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
};

export const FavoritesTagManager: React.FC<Props> = ({ isOpen, onRequestClose }) => {
  const dispatch = useAppDispatch();
  const favorites = useAppSelector((state) => state.drugs.favoriteMedications);

  const [editingFavoriteId, setEditingFavoriteId] = useState<string | null>(null);
  const [editingTags, setEditingTags] = useState<string[]>([]);
  const [editingMedication, setEditingMedication] = useState<any>(null);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState('');
  const [drugbankKey, setDrugbankKey] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);

  // Get all unique tags
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    favorites.forEach((fav) => {
      fav.tags?.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [favorites]);

  // Get tags for a favorite (from editingTags if editing, otherwise original)
  const getTags = (favoriteId: string) => {
    if (editingFavoriteId === favoriteId) {
      return editingTags;
    }
    return ((favorites.find((f) => f.id === favoriteId) as any)?.tags as string[]) ?? [];
  };

  // Filter favorites by search term
  const filteredFavorites = useMemo(() => {
    if (!filter) return favorites;
    const lowerFilter = filter.toLowerCase();
    return favorites.filter((fav: any) => {
      const summary = CPPUtils.getPrescriptionMedicationSummary(fav.value).toLowerCase();
      const hasSummaryMatch = summary.includes(lowerFilter);
      const hasTagMatch = ((fav.tags as string[]) || []).some((tag: string) =>
        tag.toLowerCase().includes(lowerFilter),
      );
      return hasSummaryMatch || hasTagMatch;
    });
  }, [favorites, filter]);

  // Group by tag for statistics
  const favoritesByTag = useMemo(() => {
    const groups: Record<string, any[]> = { Untagged: [] };
    favorites.forEach((fav: any) => {
      const tags = (fav.tags as string[]) || [];
      if (!tags || tags.length === 0) {
        groups['Untagged']!.push(fav);
      } else {
        tags.forEach((tag: string) => {
          if (!groups[tag]) groups[tag] = [];
          groups[tag]!.push(fav);
        });
      }
    });
    return groups;
  }, [favorites]);

  const handleStartEditing = (favorite: any) => {
    setEditingFavoriteId(favorite.id);
    setEditingTags((favorite.tags as string[]) || []);
    setEditingMedication(favorite);
    setDrugbankKey((prev) => prev + 1);
  };

  const handleSave = async (favoriteId: string) => {
    const favorite = favorites.find((f) => f.id === favoriteId);
    if (!favorite || !favorite.value) return;

    setLoading({ ...loading, [favoriteId]: true });

    try {
      // Save the favorite with updated tags and medication value
      await dispatch(
        api.Drugs.editFavoriteMedication({
          id: favoriteId,
          favoritePrescriptionMedication: {
            ...favorite,
            value: editingMedication?.value || favorite.value,
            tags: editingTags,
          },
        }),
      );
      // Exit edit mode
      setEditingFavoriteId(null);
      setEditingTags([]);
      setEditingMedication(null);
    } finally {
      setLoading({ ...loading, [favoriteId]: false });
    }
  };

  const handleUpdateTags = (tags: string[]) => {
    setEditingTags(tags);
  };

  const handleCancel = () => {
    setEditingFavoriteId(null);
    setEditingTags([]);
    setEditingMedication(null);
  };

  const handleDeleteFavorite = async (favoriteId: string) => {
    setLoading({ ...loading, [favoriteId]: true });
    try {
      await dispatch(api.Drugs.removeFavoriteMedication({ id: favoriteId }), {
        errorTitle: 'Remove Favorite',
      });
    } finally {
      setLoading({ ...loading, [favoriteId]: false });
    }
  };

  const handleUpdateMedication = (value: PrescriptionMedication) => {
    setEditingMedication({ ...editingMedication, value });
  };

  return (
    <>
      <DefaultModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        className="favorites-tag-manager"
        title="Manage Favorite Drugs"
        wrap={true}
        scrollable={false}
        padding={false}
        content={
          <div className="tag-manager-content">
            <div className="filter-section">
              <Icon src="magnifying-glass" width="16px" className="search-icon" />
              <input
                type="text"
                placeholder="Search medications or tags..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="search-input"
              />
              <DefaultButton
                name="Add"
                icon="plus"
                onClick={() => setShowAddModal(true)}
                className="blue"
                style={{ width: '100px' }}
              />
            </div>

            {allTags.length > 0 && (
              <div className="tag-overview">
                <strong>Your Tags:</strong>
                {allTags.map((tag) => (
                  <DefaultChip
                    key={tag}
                    element={
                      <div className="flex-row flex items-center">
                        {tag}
                        <UnreadBadge
                          unreadCount={favoritesByTag[tag]?.length || 0}
                          squished={true}
                          backgroundColor="#979797"
                          style={{ marginLeft: '5px' }}
                        />
                      </div>
                    }
                    backgroundColor="#e3f2fd"
                    textColor="#1976d2"
                    onClick={() => setFilter(tag)}
                    alt="Click to filter by this tag"
                  />
                ))}
                {(favoritesByTag['Untagged']?.length || 0) > 0 && (
                  <DefaultChip
                    text={`Untagged (${favoritesByTag['Untagged']?.length || 0})`}
                    backgroundColor="#f5f5f5"
                    textColor="#666"
                    alt="Medications without tags"
                  />
                )}
              </div>
            )}

            <div className="favorites-list">
              {filteredFavorites.length === 0 ? (
                <div className="empty-state">
                  {filter && <Icon src="magnifying-glass" width="48px" className="empty-icon" />}
                  <p>No medications found {filter ? `matching "${filter}"` : ''}</p>
                </div>
              ) : (
                filteredFavorites.map((favorite) => {
                  if (!favorite.id || !favorite.value) return null;
                  const summary = CPPUtils.getPrescriptionMedicationSummary(favorite.value);
                  const tags = getTags(favorite.id);
                  const isEditing = editingFavoriteId === favorite.id;

                  return (
                    <div
                      key={favorite.id}
                      className={`favorite-item ${isEditing ? 'editing' : ''}`}
                    >
                      {!isEditing ? (
                        <>
                          <div className="medication-info">
                            <div className="medication-name">{summary}</div>
                            <div className="action-buttons-wrapper">
                              <div className="icon-buttons">
                                <Icon
                                  src="pen"
                                  width="16px"
                                  className="action-icon"
                                  onClick={() => handleStartEditing(favorite)}
                                  alt="Edit medication and tags"
                                  showTooltip={true}
                                />
                                <ConfirmationIcon
                                  confirmText="Are you sure you want to remove this medication from your favorites?"
                                  onClick={() => handleDeleteFavorite(favorite.id!)}
                                  src="trash"
                                  width="16px"
                                  className="action-icon red"
                                  alt="Remove from favorites"
                                  showTooltip={true}
                                />
                              </div>
                            </div>
                          </div>
                          {tags?.length > 0 && (
                            <div className="tag-chips-display">
                              {tags.map((tag) => (
                                <DefaultChip
                                  key={tag}
                                  text={tag}
                                  backgroundColor="#e3f2fd"
                                  textColor="#1976d2"
                                />
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="edit-mode-content">
                          <div className="medication-form">
                            <DrugInputView
                              medication={editingMedication?.value}
                              drugbankKey={drugbankKey}
                              onFormChange={handleUpdateMedication}
                              hideFavorites={true}
                            />
                          </div>
                          <div className="tags-section">
                            <label className="section-label">Tags</label>
                            <TagInput
                              initialTags={tags}
                              onChange={(newTags) => handleUpdateTags(newTags)}
                              suggestions={allTags}
                              placeholder="Add tags..."
                              disabled={false}
                            />
                          </div>
                          <div className="edit-actions">
                            <DefaultButton name="Cancel" onClick={handleCancel} className="white" />
                            <DefaultButton
                              name="Save"
                              onClick={() => handleSave(favorite.id!)}
                              loading={loading[favorite.id!]}
                              className="blue"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        }
      ></DefaultModal>
      {showAddModal && (
        <AddToFavoritesModal
          isOpen={showAddModal}
          onRequestClose={() => setShowAddModal(false)}
          medication={undefined}
        />
      )}
    </>
  );
};

export default FavoritesTagManager;
